import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useSpring, useTransform, useVelocity } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

import { Reveal } from '../components/Reveal';
import Seo from '../components/Seo';
import { useLang } from '../i18n';
import { useSeasons } from '../lib/useSeasons';
import { Img } from '../components/Img';
import { seasonMedia, altOf } from '../lib/seasonMedia';

/**
 * I filoni come indice di una rivista: righe in Bodoni separate da filetti,
 * non tessere tutte uguali. Su desktop l'immagine del filone segue il cursore
 * (con una molla e un filo d'inerzia che la inclina nel verso del moto); sul
 * telefono la stessa immagine sta nella riga, piccola, accanto al titolo.
 */
const Indice = ({ voci }) => {
  const [attiva, setAttiva] = useState(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const molla = { stiffness: 260, damping: 30, mass: 0.5 };
  const sx = useSpring(x, molla);
  const sy = useSpring(y, molla);
  const inclina = useTransform(useVelocity(sx), [-1600, 1600], [-7, 7], { clamp: true });

  return (
    <div className="wrap pb-24 md:pb-32" onPointerMove={(e) => { x.set(e.clientX); y.set(e.clientY); }}>
      <ul data-testid="stories-index">
        {voci.map((v, i) => (
          <li key={v.to}>
            <Link
              to={v.to}
              onPointerEnter={() => setAttiva(i)}
              onPointerLeave={() => setAttiva(null)}
              className="group grid grid-cols-[4.5rem_1fr_auto] items-center gap-x-5 py-7 md:grid-cols-[1fr_minmax(0,34ch)_auto] md:gap-x-12 md:py-11"
            >
              <div className="aspect-[4/5] overflow-hidden bg-carta-2 md:hidden">
                {v.img && <Img src={v.img} alt="" width={160} sizes="72px" className="h-full w-full object-cover" />}
              </div>
              <div className="min-w-0">
                <h2 className="font-display text-[clamp(2.1rem,6.2vw,5.6rem)] leading-[0.95] tracking-[-0.025em] transition-[color,transform] duration-500 ease-[var(--morbido)] group-hover:translate-x-[0.18em] group-hover:text-marsala">
                  {v.title}
                </h2>
                <p className="prosa mt-2 text-sm md:hidden">{v.body}</p>
              </div>
              <p className="prosa hidden md:block">{v.body}</p>
              <ArrowUpRight
                aria-hidden
                strokeWidth={1.4}
                className="h-7 w-7 shrink-0 transition-transform duration-500 ease-[var(--morbido)] group-hover:rotate-45 group-hover:text-marsala md:h-10 md:w-10"
              />
            </Link>
          </li>
        ))}
      </ul>

      <motion.div
        aria-hidden
        style={{ x: sx, y: sy, rotate: inclina }}
        className="pointer-events-none fixed left-0 top-0 z-40 hidden [@media(hover:hover)_and_(pointer:fine)_and_(min-width:768px)]:block"
      >
        {/* il centraggio sta su un livello suo: framer scrive `transform` in linea
            e cancellerebbe le classi translate di Tailwind */}
        <div className="-translate-x-1/2 -translate-y-1/2">
        <motion.div
          initial={false}
          animate={{ opacity: attiva === null ? 0 : 1, scale: attiva === null ? 0.86 : 1 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="relative aspect-[4/5] w-[clamp(220px,20vw,320px)] overflow-hidden bg-carta-2"
        >
          {voci.map((v, i) =>
            v.img ? (
              <Img
                key={v.to}
                src={v.img}
                alt=""
                width={640}
                sizes="320px"
                className={`absolute inset-0 h-full w-full object-cover transition-[opacity,transform] duration-700 ease-[var(--morbido)] ${
                  attiva === i ? 'scale-100 opacity-100' : 'scale-110 opacity-0'
                }`}
              />
            ) : null
          )}
        </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default function Stories() {
  const { t, lang } = useLang();
  const { active: stagione } = useSeasons();
  const foto = stagione ? seasonMedia(stagione.slug)?.storie : null;

  const strands = [
    {
      to: '/storie/articoli',
      title: t('stories.articles'),
      body: t('stories.articlesBody'),
      img: '/assets/ispirazioni/per-la-tavola-1.webp',
    },
    {
      to: '/storie/shades',
      title: t('stories.shades'),
      body: t('stories.shadesBody'),
      img: '/assets/shades/ambient.webp',
    },
    {
      to: '/storie/victionary',
      title: t('stories.victionary'),
      body: t('stories.victionaryBody'),
      img: '/assets/collezioni/victionary.webp',
    },
    ...(stagione && !foto
      ? [{ to: stagione.path, title: t('stories.xmas'), body: t('stories.xmasBody') }]
      : []),
  ];

  return (
    <>
      <Seo title={t('stories.seoTitle')} description={t('stories.seoDesc')} />

      <header className="wrap pt-16 pb-10 md:pt-24">
        <h1 className="display-xl mt-3">{t('stories.title')}</h1>
        <p className="prosa mt-5 max-w-2xl">{t('stories.intro')}</p>
      </header>

      {foto && (
        <div className="wrap pb-8">
          <Reveal>
            <Link to={stagione.path} data-testid="stories-featured" className="group block">
              <div className="relative aspect-[4/3] overflow-hidden bg-carta-2 sm:aspect-[21/9]">
                <Img
                  src={foto.src}
                  alt={altOf(foto, lang)}
                  width={1680}
                  aspectRatio={21 / 9}
                  sizes="100vw"
                  className="img-zoom h-full w-full object-cover"
                />
              </div>
              <div className="mt-6 flex flex-wrap items-baseline justify-between gap-4 pb-8">
                <h2 className="font-display text-[clamp(2rem,5vw,3.4rem)] leading-[1.04] transition-colors group-hover:text-marsala-dark">
                  {t('stories.xmas')}
                </h2>
                <p className="prosa max-w-[48ch]">{t('stories.xmasBody')}</p>
              </div>
            </Link>
          </Reveal>
        </div>
      )}

      <Indice voci={strands} />
    </>
  );
}
